/**
 * Listens for Rust `session_engine:consult_note` lifecycle (HAR-1777), tracks
 * Snowplow/Datadog `note_generation_started|completed|failed` plus the
 * `note_generate` client attempt, and refreshes the session detail cache when
 * a Rust-driven note finishes generating.
 *
 * Mount once on desktop. Do not wire this into stream-response / SBS /
 * noteGenerationEventTracking — those stay Segment/perf-only.
 */
import { getDefaultStore } from 'jotai';
import { useEffect } from 'react';

import { getChronicleDeviceFileNameForSession } from '@/atoms/chronicle/session-device-files';
import {
  offlineDeletedLocalSessionIdsAtom,
  offlineSyncQueueAtom,
} from '@/atoms/offline-recording/sync';
import { findOfflineSyncQueueItem } from '@/hooks/desktop/offline-sync-queue-lookup';
import {
  beginDesktopNoteGenerateAttempt,
  cancelDesktopNoteGenerateAttempt,
  completeDesktopNoteGenerateAttempt,
  failDesktopNoteGenerateAttempt,
} from '@/lib/telemetry/noteGenerateTracking';
import type {
  NoteGenerationAnalyticsSource,
  NoteGenerationTrigger,
} from '@/models/analytics/features/notes';
import { isTauri } from '@/tauri/utils';
import { stripDeviceFileNameForAnalytics } from '@/utils/analytics/strip-device-file-name';
import {
  trackNoteGenerationCompletedAnalytics,
  trackNoteGenerationFailedAnalytics,
  trackNoteGenerationStartedAnalytics,
} from '@/utils/analytics/track-note-generation-analytics';
import { logger } from '@/utils/logger/logger';
import { managedListen, type ManagedUnlistenFn } from '@heidi/plugin-core';
import { invalidateSessionById } from '@heidi/session/data';

/** Keep in lockstep with `CONSULT_NOTE_LIFECYCLE_EVENT` in note_trigger.rs. */
export const CONSULT_NOTE_LIFECYCLE_EVENT = 'session_engine:consult_note';

export type ConsultNoteLifecyclePhase = 'started' | 'completed' | 'failed';

/** CamelCase payload from Rust (`#[serde(rename_all = "camelCase")]`). */
export type ConsultNoteLifecyclePayload = {
  phase: ConsultNoteLifecyclePhase;
  sessionId: string;
  /** Contract trigger from Rust; unknown/missing values fail closed in mapTrigger. */
  generationTrigger?: string | null;
  source: string;
  /**
   * Offline local session id from Rust (`task_id`). Preferred over the FE
   * sync-queue reverse lookup — delete removes the queue row with the tombstone.
   */
  localSessionId?: string | null;
  /** Rust wall-clock Unix ms when `started` was emitted. */
  startedAtMs?: number | null;
  /**
   * Whole-ladder elapsed ms (backoff / unbounded `wait_for_token` included) —
   * not single-attempt SSE latency. Snowplow `duration_ms` / Datadog
   * `total_elapsed_ms`. See metrics-contract §Note funnel caveats.
   */
  durationMs?: number | null;
  errorCode?: string | null;
};

function mapSource(source: string): NoteGenerationAnalyticsSource | null {
  if (source === 'chronicle') return 'chronicle';
  if (source === 'offline_recording') return 'offline_recording';
  return null;
}

/**
 * Fail-closed like `mapSource`: only the three contract values pass through.
 * Unknown / missing triggers warn and stay undefined — never silently rewrite
 * to `offline_sync_auto_trigger` (that value filters the offline funnel).
 */
function mapTrigger(trigger: string | undefined): NoteGenerationTrigger | undefined {
  if (
    trigger === 'offline_sync_auto_trigger' ||
    trigger === 'manual_create' ||
    trigger === 'retry'
  ) {
    return trigger;
  }
  if (trigger !== undefined && trigger !== '') {
    logger.warn({
      msg: '[consult_note_lifecycle] unknown generation_trigger',
      generationTrigger: trigger,
    });
  }
  return undefined;
}

function resolveOfflineQueueItem(serverSessionId: string) {
  const queue = getDefaultStore().get(offlineSyncQueueAtom);
  return findOfflineSyncQueueItem(queue, serverSessionId);
}

function isDeletedLocalSession(localSessionId: string): boolean {
  return getDefaultStore()
    .get(offlineDeletedLocalSessionIdsAtom)
    .includes(localSessionId);
}

/** Non-empty Rust payload value, else undefined (so queue fallback can apply). */
function payloadLocalSessionId(
  localSessionId: ConsultNoteLifecyclePayload['localSessionId']
): string | undefined {
  if (typeof localSessionId === 'string' && localSessionId !== '') {
    return localSessionId;
  }
  return undefined;
}

/**
 * Prefer Rust-carried localSessionId (survives queue-row delete). Fall back to
 * the sync-queue reverse lookup only when the wire field is absent.
 */
function resolveOfflineLocalSessionId(
  source: NoteGenerationAnalyticsSource,
  payloadLocalId: string | undefined,
  queueLocalId: string | undefined
): string | undefined {
  if (source !== 'offline_recording') {
    return undefined;
  }
  return payloadLocalId ?? queueLocalId;
}

export function handleConsultNoteLifecycleEvent(
  payload: ConsultNoteLifecyclePayload
): void {
  const source = mapSource(payload.source);
  if (!source) {
    logger.warn({
      msg: '[consult_note_lifecycle] unknown source',
      source: payload.source,
    });
    return;
  }
  const generation_trigger = mapTrigger(payload.generationTrigger ?? undefined);
  const device_file_name = stripDeviceFileNameForAnalytics(
    getChronicleDeviceFileNameForSession(payload.sessionId)
  );

  const offlineQueueItem =
    source === 'offline_recording'
      ? resolveOfflineQueueItem(payload.sessionId)
      : undefined;
  const offlineLocalSessionId = resolveOfflineLocalSessionId(
    source,
    payloadLocalSessionId(payload.localSessionId),
    offlineQueueItem?.localSessionId
  );
  // Sync/upload retries (MAX_SYNC_ATTEMPTS) are useful on Datadog as
  // `attempt_count`, but must NOT land in Snowplow `attempt_number` (Iglu /
  // iOS = generation attempts). Omit Snowplow until a true ladder count exists.
  // Queue miss is harmless here — attempt_count is optional.
  const syncAttemptCount =
    typeof offlineQueueItem?.attemptCount === 'number'
      ? offlineQueueItem.attemptCount
      : undefined;

  // Match useOfflineRecordingEvents: user-deleted offline rows must not emit.
  // Key on Rust-carried (or queue-fallback) local id — delete removes the row
  // and writes the tombstone together.
  if (
    typeof offlineLocalSessionId === 'string' &&
    isDeletedLocalSession(offlineLocalSessionId)
  ) {
    cancelDesktopNoteGenerateAttempt(payload.sessionId);
    return;
  }

  const startedAtMs =
    typeof payload.startedAtMs === 'number' ? payload.startedAtMs : Date.now();
  const durationMs =
    typeof payload.durationMs === 'number' ? payload.durationMs : undefined;
  // Prefer Rust startedAtMs + durationMs so end/start/duration agree on one
  // clock; FE Date.now() only when either half is missing.
  const noteGenerationEndTime =
    typeof payload.startedAtMs === 'number' && durationMs != null
      ? payload.startedAtMs + durationMs
      : Date.now();

  switch (payload.phase) {
    case 'started':
      beginDesktopNoteGenerateAttempt(payload.sessionId, source);
      trackNoteGenerationStartedAnalytics({
        session_id: payload.sessionId,
        local_session_id: offlineLocalSessionId,
        platform: 'desktop',
        device_file_name,
        note_generation_start_time: startedAtMs,
        generation_trigger,
        source,
        attempt_count: syncAttemptCount,
      });
      return;
    case 'completed':
      // The note body renders off `session.consult_note.result`, which arrives
      // on `[GET_SESSION_BY_ID, { sessionId }]` — so this is the only moment
      // anything client-side learns the note exists.
      //
      // The sync-complete path cannot cover it. `offline_recording:synced` is
      // emitted on `upload_complete` (`status_for_processing_step` treats
      // `triggering_note` as no lifecycle transition at all), so the refetch it
      // already fires lands strictly BEFORE generation is even triggered and
      // resolves an empty note. Nothing fired afterwards, which is why the note
      // only appeared once the clinician navigated away and back and remounted
      // the query.
      //
      // Ahead of the tracking call on purpose: a throwing analytics transport
      // must not be what costs the clinician their note.
      //
      // Offline only, though the event carries Chronicle too. Chronicle already
      // invalidates this exact key from its own `session_engine:note_triggered`
      // handler, which Rust emits immediately after this one on every path that
      // reaches it — so a second invalidate here buys nothing and, because
      // `invalidateQueries` cancels an in-flight refetch by default, would
      // cancel and restart the GET that handler just started. Offline has no
      // `note_triggered` consumer (`onNoteTriggered` bails on a non-Chronicle
      // source), which is precisely why it was the path left with no signal.
      //
      // Not an extra refetch for an ordinary session either: a note generated
      // in the webview streams its result into the cache over SSE and emits
      // nothing here at all.
      if (source === 'offline_recording') {
        invalidateSessionById(payload.sessionId);
      }
      completeDesktopNoteGenerateAttempt(payload.sessionId, source);
      trackNoteGenerationCompletedAnalytics({
        session_id: payload.sessionId,
        local_session_id: offlineLocalSessionId,
        platform: 'desktop',
        device_file_name,
        duration_ms: durationMs,
        note_generation_end_time: noteGenerationEndTime,
        generation_trigger,
        source,
        attempt_count: syncAttemptCount,
      });
      return;
    case 'failed': {
      // The attempt closes for both sources; only Snowplow skips offline.
      failDesktopNoteGenerateAttempt(payload.sessionId, source, payload.errorCode);
      // Offline failures are already tracked from `offline_recording:note_failed`
      // (useOfflineRecordingEvents → trackOfflineNoteGenerationFailed). Claiming
      // them here double-counts Snowplow `note_generation_failed` with near-
      // identical payloads. Keep started/completed here; fold offline failed
      // in when that legacy emitter is retired (HAR-1777 follow-up).
      if (source === 'offline_recording') return;

      // Terminal emit (outside retry) — will_retry is false by construction.
      // Chronicle has no other Snowplow failed emitter today.
      // No attempt_number / attempt_count here: Rust lifecycle carries no ladder
      // attempt count, and offlineQueueItem is unreachable on this branch
      // (offline failed returns above).
      trackNoteGenerationFailedAnalytics({
        session_id: payload.sessionId,
        platform: 'desktop',
        device_file_name,
        generation_trigger,
        source: 'chronicle',
        error_code: payload.errorCode ?? null,
        will_retry: false,
        duration_ms: durationMs,
      });
      return;
    }
    default: {
      const _exhaustive: never = payload.phase;
      logger.warn({
        msg: '[consult_note_lifecycle] unknown phase',
        phase: _exhaustive,
      });
    }
  }
}

export function useConsultNoteLifecycleListener(): void {
  useEffect(() => {
    if (!isTauri()) return;

    let cancelled = false;
    let unlisten: ManagedUnlistenFn | null = null;

    void managedListen<ConsultNoteLifecyclePayload>(
      CONSULT_NOTE_LIFECYCLE_EVENT,
      (event) => {
        handleConsultNoteLifecycleEvent(event.payload);
      }
    )
      .then((fn) => {
        if (cancelled) {
          void fn();
          return;
        }
        unlisten = fn;
      })
      .catch((error: unknown) => {
        logger.warn({
          msg: '[consult_note_lifecycle] failed to listen',
          error: error instanceof Error ? error.message : String(error),
        });
      });

    return () => {
      cancelled = true;
      if (unlisten) void unlisten();
    };
  }, []);
}
